import { useEffect, useState } from 'react';
import moment from 'moment';
import { EPagination, ETable } from 'libs/ui';
import { FilePenLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { TChargeUserType } from 'types';
import { getRole } from 'utils/routeUtility';
import ESwitch from 'libs/ui/src/Switch/ESwitch';
import { usePagination } from 'hook/usePagination';
import { useHotelMarkupsData } from 'libs/data-access';
import { humanizeText, showToast } from 'utils/utility';

import { IHotelMarkupList } from '../Hooks/types';

interface IProps {
    userChargeType: TChargeUserType;
};

export default function HotelMarkList({ userChargeType }: IProps) {

    const navigate = useNavigate();

    const {
        getHotelMarkupsData,
        patchHotelMarkupsData
    } = useHotelMarkupsData();


    const [isLoadingIndex, setIsLoadingIndex] = useState<number | undefined>(undefined);
    const [paginationQueryParams, setPaginationQueryParams] = useState<{
        id: number | null;
        date: string | null;
    }>({
        id: null,
        date: null
    });
    const [sortingDetails, setSortingDetails] = useState<{
        sortBy: "id" | "createdDate";
        sortingOrder: "ASC" | "DESC";
    }>({
        sortBy: "createdDate",
        sortingOrder: "DESC"
    });


    // API CALLS
    const updateStatus = async (id: number, status: string, index: number) => {
        setIsLoadingIndex(index);

        const response = await patchHotelMarkupsData(`/${id}/${status}`, {});

        if (response.isSuccess) {
            showToast({
                type: "success",
                message: "Markup status updated successfully",
            });
            setDataList(prev => prev.map(
                (each, i) =>
                    i === index
                        ?
                        { ...each, status: status === "active" ? true : false }
                        :
                        each
            ));
        } else {
            showToast({
                type: "error",
                message: response.message ?? "Something went wrong",
            })
        };

        setIsLoadingIndex(undefined);
    };


    const {
        dataList: hotelMarkupList,
        setDataList,
        isLoading,
        setSearchText,
        searchText,
        goToNextPage,
        goToPreviousPage,
        disablePrev,
        disableNext,
        initialLoading,
        setTriggerNextPage,
        setTriggerPreviousPage,
        goToInitialPage
    } = usePagination<IHotelMarkupList>({
        fetchListData: getHotelMarkupsData,
        maxLimit: 5,
        // sortby: sortingDetails?.sortBy,
        sortingOrder: sortingDetails?.sortingOrder,
        extraQueryParams: {
            userType: userChargeType
        } as Record<string, string | number | boolean>,
        paginationParams: paginationQueryParams as Record<string, string | number | boolean>,
        dependencies: []
    });


    return (
        <div>
            <ETable loading={isLoading}>
                <thead>
                    <tr>
                        <th>Supplier</th>
                        <th>Types</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Minimum Gross Price</th>
                        <th>Maximum Gross Price</th>
                        <th>Charges</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        hotelMarkupList.map((each, index) => (
                            <tr key={index + "hotel-markup-list"}>
                                <td>
                                    {each.hotelSuppliers?.length ? each.hotelSuppliers?.map((each) => each.key).join(", ") : "All"}
                                </td>
                                <td>
                                    {each.hotelTypes?.length ? each.hotelTypes?.map((each) => each.key).join(", ") : "All"}
                                </td>
                                <td>
                                    {each.startDate ? moment(each.startDate).format("DD-MM-YYYY") : "All"}
                                </td>
                                <td>
                                    {each.endDate ? moment(each.endDate).format("DD-MM-YYYY") : "All"}
                                </td>
                                <td>
                                    {each.minimumGrossPrice ? each.minimumGrossPrice : "All"}
                                </td>
                                <td>
                                    {each.maximumGrossPrice ? each.maximumGrossPrice : "All"}
                                </td>
                                <td>
                                    {each.markupChargeWithAmountTypeResponses?.map((each, index) => (
                                        <div key={index + "markup-charge-with-amount-type-responses"}>
                                            {`${each.markupFormCharge.key}: ${each.amount} - ${humanizeText(each.feeType)}`}
                                        </div>
                                    ))}
                                </td>
                                <td>
                                    <ESwitch
                                        label={each.status ? "Active" : "Inactive"}
                                        loading={isLoadingIndex === index}
                                        checked={each.status}
                                        onChange={(value) => updateStatus(each.id, value ? "active" : "inActive", index)}
                                    />
                                </td>
                                <td>
                                    <FilePenLine
                                        className="cursor-pointer text-green-500 hover:bg-green-200 transition-all duration-300 rounded-full p-1"
                                        onClick={() => {
                                            navigate(`/admin-transactions/markup_and_commission/markup/b2c/hotel/${userChargeType}?mno=${each.id}`);
                                        }}
                                    />
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </ETable>

            <EPagination
                isLoading={isLoading}
                disablePrev={disablePrev}
                disableNext={disableNext}
                goToNextPage={() => {
                    // setPaginationQueryParams({
                    //   ...paginationQueryParams,
                    //   date: hotelMarkupList[hotelMarkupList.length - 1].bookingDate
                    // });
                    setTriggerNextPage(true);
                }}
                goToPreviousPage={() => {
                    // setPaginationQueryParams({
                    //   ...paginationQueryParams,
                    //   date: hotelMarkupList[0].bookingDate
                    // });
                    setTriggerPreviousPage(true);
                }}
            />
        </div>
    );

}
